import store from '../state';

export interface MushroomSpeciesInfo {
  id: string;
  name: string;
  scientificName: string;
  host: string;
  season: string;
  tempWindow: string;
  rainTrigger: string;
  lagDays: string;
  harvestWindow: string;
  color: string;
  tips: string;
}

export const SPECIES_CATALOG: Record<string, MushroomSpeciesInfo> = {
  general_flush: {
    id: 'general_flush',
    name: 'All Wild Fungi (General Flush)',
    scientificName: 'Macrofungi Assemblage',
    host: 'Mixed Hardwood & Conifer Forests',
    season: 'Spring through Late Autumn',
    tempWindow: '60°F - 78°F (Optimal)',
    rainTrigger: '≥ 0.75" soaking rain',
    lagDays: '5 - 9 days post-rain',
    harvestWindow: '5 - 7 days',
    color: '#ff9800',
    tips: 'Broad composite index tracking all active macrofungal flushes across the continent.'
  },
  chicken_of_the_woods: {
    id: 'chicken_of_the_woods',
    name: 'Chicken of the Woods',
    scientificName: 'Laetiporus sulphureus / cincinnatus',
    host: 'Dead or dying oaks, hardwoods, stumps',
    season: 'Summer to Early Fall (June - Oct)',
    tempWindow: '70°F - 84°F (Warm daytime)',
    rainTrigger: '≥ 0.75" - 1.25" warm storms',
    lagDays: '5 - 8 days post-rain',
    harvestWindow: '4 - 6 days',
    color: '#ff6d00',
    tips: 'Bright yellow-orange brackets on oaks. Check 5-7 days after heavy summer thunderstorms.'
  },
  morel: {
    id: 'morel',
    name: 'Morels (Spring)',
    scientificName: 'Morchella esculenta / elata',
    host: 'Dead elms, ash, apple orchards, burn scars',
    season: 'Spring (March South, May North/Rockies)',
    tempWindow: 'Soil: 50°F - 58°F at 2-4" depth',
    rainTrigger: '≥ 0.50" spring rain',
    lagDays: '4 - 7 days post-rain',
    harvestWindow: '7 - 10 days',
    color: '#e6c387',
    tips: 'Strictly soil-temperature dependent. Hunt when daytime highs reach 60s°F and nights stay in 50s°F.'
  },
  chanterelle: {
    id: 'chanterelle',
    name: 'Golden Chanterelles',
    scientificName: 'Cantharellus cibarius / formosus',
    host: 'Mossy hardwood/conifer forest soil (mycorrhizal)',
    season: 'Summer to Fall (July East, Nov PNW)',
    tempWindow: '65°F - 80°F (Sustained mild)',
    rainTrigger: '≥ 1.50" - 2.50" cumulative deep soak',
    lagDays: '10 - 16 days post-rain',
    harvestWindow: '10 - 21 days',
    color: '#fbc02d',
    tips: 'Slow growing. Emerges 10-14 days after deep ground-saturating rains. Long shelf life.'
  },
  porcini_bolete: {
    id: 'porcini_bolete',
    name: 'King Bolete / Porcini',
    scientificName: 'Boletus edulis sensu lato',
    host: 'Spruce, fir, pine, hemlock, oak',
    season: 'Late Summer to Fall (Aug Rockies, Oct PNW)',
    tempWindow: 'Cold drop: Night temps dipping ≤ 50°F',
    rainTrigger: '≥ 1.00" - 1.50" soaking rain',
    lagDays: '5 - 9 days post-cold shock',
    harvestWindow: '3 - 6 days (bug sensitive)',
    color: '#8d6e63',
    tips: 'Requires thermal shock. Pops quickly after cold overnight autumn rains in conifer forests.'
  },
  maitake_hen: {
    id: 'maitake_hen',
    name: 'Hen of the Woods / Maitake',
    scientificName: 'Grifola frondosa',
    host: 'Base and root flare of mature oaks',
    season: 'Late Summer to Mid Autumn (Sept - Oct)',
    tempWindow: 'Night temps: 48°F - 58°F',
    rainTrigger: '≥ 1.00" - 2.00" autumn soaking',
    lagDays: '10 - 14 days post-rain',
    harvestWindow: '7 - 14 days',
    color: '#795548',
    tips: 'Huge rosettes up to 20+ lbs at the base of large oaks. Consistent annual producer.'
  },
  lions_mane: {
    id: 'lions_mane',
    name: "Lion's Mane / Bear's Head",
    scientificName: 'Hericium erinaceus / americanum',
    host: 'Wounded living or fallen beech, maple, oak',
    season: 'Autumn to Early Winter (Sept - Dec)',
    tempWindow: '50°F - 62°F, RH ≥ 80%',
    rainTrigger: '≥ 1.00" antecedent hydration',
    lagDays: '8 - 14 days post-rain',
    harvestWindow: '7 - 14 days',
    color: '#eceff1',
    tips: 'Requires high ambient humidity to prevent icicle-like spines from drying out.'
  },
  oyster: {
    id: 'oyster',
    name: 'Oyster Mushrooms',
    scientificName: 'Pleurotus ostreatus',
    host: 'Fallen aspen, poplar, beech, willow logs',
    season: 'Spring and Autumn through Frost',
    tempWindow: '45°F - 58°F (Cold shock stimulated)',
    rainTrigger: '≥ 0.75" rain',
    lagDays: '3 - 5 days post-rain',
    harvestWindow: '3 - 5 days',
    color: '#cfd8dc',
    tips: 'Fastest flusher in the wild. Pops rapidly on decaying hardwood logs within 72h of rain.'
  }
};

export class MushroomService {
  private static summaryCache: any = null;
  private static lastFetchTime: number = 0;

  public static async getSummary(): Promise<any> {
    const now = Date.now();
    if (this.summaryCache && (now - this.lastFetchTime < 300000)) {
      return this.summaryCache;
    }

    try {
      const manifest = store.getState().manifest;
      const tileBase = manifest?.tile_base ? (manifest.tile_base.startsWith('http') ? manifest.tile_base : `/${manifest.tile_base.replace(/^\/+/, '')}`) : '/tiles';
      const url = `${tileBase}/observed/mushrooms/mushrooms_summary.json`;
      const res = await fetch(url);
      if (res.ok) {
        this.summaryCache = await res.json();
        this.lastFetchTime = now;
        return this.summaryCache;
      }
    } catch (e) {
      console.warn('Could not fetch mushrooms_summary.json:', e);
    }

    // Fallback baseline metadata
    return {
      date: new Date().toISOString().split('T')[0],
      max_flush_score: 95.0,
      mean_active_score: 58.0,
      prime_harvest_cells: 2500,
      hotspot_regions: [
        { region: 'Pacific Northwest', status: 'Prime Harvest Window', species: ['Chanterelles', 'Porcini', 'Oysters'] },
        { region: 'Southern Appalachians', status: 'Peak Moisture Flush', species: ['Chicken of the Woods', 'Maitake', 'Chanterelles'] },
        { region: 'Upper Midwest & Great Lakes', status: 'Early Autumn Flush', species: ['Lion\'s Mane', 'Hen of the Woods'] }
      ]
    };
  }

  public static getStageInfo(score: number): { stage: string; color: string; advice: string } {
    if (score >= 85) {
      return {
        stage: 'Super-Flush Hotspot',
        color: '#d32f2f',
        advice: 'Peak conditions! Substantial flushes active right now. Prime harvest window.'
      };
    }
    if (score >= 65) {
      return {
        stage: 'Prime Harvest Window',
        color: '#ff6d00',
        advice: 'Active fruiting bodies expanding. Excellent foraging potential.'
      };
    }
    if (score >= 35) {
      return {
        stage: 'Emerging Flush / Pinning',
        color: '#fbc02d',
        advice: 'Primordia forming or early buttons emerging. Check back in 2-4 days.'
      };
    }
    if (score >= 15) {
      return {
        stage: 'Early Moisture / Inoculating',
        color: '#4caf50',
        advice: 'Recent rainfall detected. Mycelium active; priming for future flush.'
      };
    }
    return {
      stage: 'Sub-Threshold / Dormant',
      color: '#78909c',
      advice: 'Dry or unseasonable temperatures. Low macrofungal activity.'
    };
  }
}
